/**
 * `formatLoggedAs` (extracted for from `HoldModal`, where it shipped in CDMX-18412).
 *
 * Pure. Explicit locale; optional IANA `timeZone` so stories and tests can pin the rendered instant
 * and assert an exact string instead of whatever the CI box's zone produces.
 *
 * Two styles, because the two case-action modals were specified differently and the ticket wins in
 * each case:
 * `'short'`   -> "Jul 13, 12:49 PM"     — Hold (CDMX-18412), unchanged.
 * `'numeric'` -> "07/13/2026 10:42 AM"  — Transfer, whose ticket gives that exact
 *    numeric example. The design review (finding C6) called reusing Hold's abbreviated
 *    form "a preference over the ticket's explicit example", so the style is a parameter rather
 *    than a house rule. Aligning Hold onto `'numeric'` later is then a one-word change.
 *
 * Lives in `molecules/CaseActionModal/` rather than inside either modal so the second consumer does
 * not import from the first (an inverted Transfer->Hold dependency — review finding A9).
 */
export type LoggedAsStyle = 'short' | 'numeric';

export const formatLoggedAs = (
  at: Date,
  timeZone?: string,
  style: LoggedAsStyle = 'short'
): string => {
  const formatted = new Intl.DateTimeFormat('en-US', {
    ...(style === 'numeric'
      ? ({ month: '2-digit', day: '2-digit', year: 'numeric' } as const)
      : ({ month: 'short', day: 'numeric' } as const)),
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone
  }).format(at);
  // `Intl` joins date and time with ", " in both styles. The numeric form the ticket
  // shows ("07/13/2026 10:42 AM") separates them with a plain space instead. Only the numeric
  // style is rewritten, so Hold's shipped "Jul 13, 12:49 PM" is byte-for-byte unchanged.
  return style === 'numeric' ? formatted.replace(', ', ' ') : formatted;
};
